/**
 * Daily Profile Checks — verifies the prior day's aggregate data on the
 * tester's profile (distance, steps, active minutes, calories).
 *
 * Note on day numbering: when a tester verifies day 3's data, the
 * verification row uses testing_day = 3 (the day being verified). The
 * check becomes available the morning of day 4, and the check itself
 * is shown on the day after (the prior day's totals are what's verified).
 *
 * On day N, the most recent unverified day to surface is day N-1.
 *
 * Past days where any of the 4 metrics are missing get stacked. Each
 * past-day block has a "Couldn't sync this day" escape that bulk-skips
 * the four metrics for that day.
 */

import { format, addDays } from "date-fns";
import type { Tester, Verification, VerificationStatus, ConditionConfigRow } from "../../types";
import { Section, StaleDayHeader, CheckRow } from "../Section";
import { YesNoNaButtons } from "../VerificationButtons";
import { openAsanaBugForm } from "../../lib/asana";

/**
 * condition_config labels are bare nouns ("Distance", "Steps"), so the verb
 * has to agree with the label the admin entered. A trailing "s" is a good
 * enough plural test for the metric names in use (Distance / Steps / Active
 * Minutes / Calories). If a singular label ending in "s" is ever added, this
 * reads wrong, and the fix at that point is to move the full phrase into the
 * condition_config label rather than to grow the heuristic.
 */
function looksOrLook(label: string): string {
  return /s$/i.test(label.trim()) ? "look" : "looks";
}

interface DailyProfileChecksSectionProps {
  profile: Tester;
  currentDayNum: number;
  cohortStart: Date;
  asanaUrl: string;
  dailyProfileChecks: ConditionConfigRow[]; // from condition_config table, section='daily_profile'
  getStatus: (key: string, day: number) => Verification | null;
  setStatus: (input: {
    conditionKey: string;
    testingDay: number;
    status: VerificationStatus;
  }) => Promise<void>;
}

export function DailyProfileChecksSection({
  profile,
  currentDayNum,
  cohortStart,
  asanaUrl,
  dailyProfileChecks,
  getStatus,
  setStatus,
}: DailyProfileChecksSectionProps) {
  // Section first becomes available on day 2 (verifying day 1's data).
  if (currentDayNum < 2) return null;
  if (dailyProfileChecks.length === 0) return null;

  // Find prior days (1..currentDay-1) that have any incomplete check.
  // Day 1 here means "Day 1's data" which is verified on Day 2+.
  const incompleteDays: number[] = [];
  for (let d = 1; d < currentDayNum; d++) {
    const anyMissing = dailyProfileChecks.some((c) => !getStatus(c.condition_key, d));
    if (anyMissing) incompleteDays.push(d);
  }

  if (incompleteDays.length === 0) {
    // All past days are accounted for. Render a calm "all caught up" state
    // until the next day of data becomes available.
    return (
      <Section title="Yesterday's profile data" meta="Caught up">
        <div className="px-3 py-3 text-[12px] text-zinc-500 italic">
          You're caught up on profile data verification. Tomorrow's check appears in the
          morning.
        </div>
      </Section>
    );
  }

  const handleSelect = async (
    dataDay: number,
    check: ConditionConfigRow,
    status: VerificationStatus,
  ) => {
    await setStatus({ conditionKey: check.condition_key, testingDay: dataDay, status });
    if (status === "issue_found") {
      const dayDate = format(addDays(cohortStart, dataDay - 1), "EEE, MMM d");
      const dayLabel = `Day ${dataDay} of ${
        profile.tester_type === "Volunteer" ? "14" : "7"
      }`;
      openAsanaBugForm(asanaUrl, {
        email: profile.email,
        wordpressUserId: profile.wordpress_user_id,
        dayLabel,
        dayDate,
        conditionLabel: `Profile - ${check.label} (Day ${dataDay} - ${dayDate})`,
      });
    }
  };

  const handleCouldNotSync = async (dataDay: number) => {
    // Bulk-skip only the checks the tester hasn't answered yet for this day.
    // Overwriting an existing answer would silently discard a Yes/No, or a
    // "Not sent" recorded against a metric their platform never provides.
    const unanswered = dailyProfileChecks.filter(
      (c) => !getStatus(c.condition_key, dataDay),
    );
    await Promise.all(
      unanswered.map((c) =>
        setStatus({ conditionKey: c.condition_key, testingDay: dataDay, status: "skipped" }),
      ),
    );
  };

  return (
    <Section title="Profile data — catching up" meta={`${incompleteDays.length} day${incompleteDays.length === 1 ? "" : "s"} pending`}>
      {incompleteDays.map((d) => {
        const date = format(addDays(cohortStart, d - 1), "EEE, MMM d");
        return (
          <div key={d}>
            <StaleDayHeader label={`Day ${d} · ${date} totals`} />
            {dailyProfileChecks.map((check) => {
              const row = getStatus(check.condition_key, d);
              return (
                <CheckRow
                  key={check.condition_key}
                  label={`${check.label} ${looksOrLook(check.label)} correct`}
                  flagged={row?.status === "issue_found"}
                  trailing={
                    <YesNoNaButtons
                      status={row?.status ?? null}
                      onSelect={(s) => handleSelect(d, check, s)}
                      naLabel="Not sent"
                    />
                  }
                />
              );
            })}
            <div className="px-3 py-2 border-t border-zinc-100">
              <button
                type="button"
                onClick={() => handleCouldNotSync(d)}
                className="text-[11px] text-zinc-500 hover:text-zinc-800 transition"
              >
                ⚠ Couldn't sync this day — skip all checks
              </button>
            </div>
          </div>
        );
      })}

      {/* Mirrors the Challenge section's footer, which explains its own N/A. */}
      <div className="px-3 py-2 text-[10px] text-zinc-400 italic border-t border-zinc-100">
        &ldquo;Not sent&rdquo; means your device never provides this metric. Use
        &ldquo;No&rdquo; only when a number is present and wrong, or when a metric that
        worked yesterday is blank today.
      </div>
    </Section>
  );
}
